/**
 * Whether a proposed run can succeed, asked before it is started (IM-36).
 *
 * Most failed runs fail on something that was knowable in advance: a lapsed
 * grant, a dispatcher that is not deployed in the region the run is going to, a
 * region with no state bucket, a variable the installer refuses, an id it
 * cannot build resource names from. Every one of those already has a rule
 * somewhere — the onboarding assessment per region, the run-document
 * validators in `deployment/` — and each was reported on a different screen,
 * or only after a container had started in someone else's account. This puts
 * them into one answer for one specific run.
 *
 * ## It reuses rules, it does not restate them
 *
 * Every check here is an existing function's answer, re-shaped. The document
 * checks call the validators the API start path calls; the account checks read
 * the onboarding assessment's steps and its `blockedBy`, which is the rule
 * `canRun` applies. There is no rule in this file about what a grant or a
 * dispatcher version means, so the preflight and the onboarding screen cannot
 * come to disagree about whether a run will work.
 *
 * ## Blocking and advisory are different questions
 *
 * A check is **blocking** only when a run started now is certain to fail, or is
 * one the product is built to refuse: the grant has lapsed, the dispatcher is
 * missing or below the floor, a refused variable. Everything that is merely
 * worth knowing — a grant with a week left, a dispatcher a release behind, a
 * stale redactor — is **advisory**. The same visible-versus-blocking split the
 * onboarding screen draws, for the same reason: refusing a run AWS would permit
 * is this console overriding the thing that actually decides.
 *
 * ## Unknown is not pass, and it is never blocking
 *
 * A fact that could not be read is reported as `unknown`, with what to do
 * about it — never silently as `pass`, which is the onboarding rule. But it is
 * never *blocking* on its own either: a read that timed out must not stop a run
 * the customer's account would have accepted. The account has the final word
 * whatever this says, because the grant is enforced by AWS rather than by code.
 */
import { assertNever } from '../assert.js';
import { missingRequiredVariables, searchBackendProblem, deploymentIdProblem, } from '../deployment/config.js';
import { refusedVariableProblem } from '../deployment/refused.js';
import { regionConflict, regionProblem } from '../deployment/regions.js';
import { ONBOARDING_STEPS, STEP_SCOPE } from '../onboarding/onboarding.js';
import { chartInstallerMatch, installerMatchMessage } from '../deployment/installer-match.js';
import { mayCreateInstance } from './run.js';
import { newDeploymentIdProblem } from '../boundary/boundary.js';
import { requiredBoundaryProblem } from '../boundary/required.js';
/**
 * Every check a preflight can report, in the order someone would fix them.
 *
 * Not every check appears in every answer: the account checks are absent when
 * the account was not asked, and the two policy checks only apply to a run that
 * would create an instance. An id that is absent is not an id that passed.
 */
export const PREFLIGHT_CHECK_IDS = [
    'refused-variables',
    'required-variables',
    'deployment-id',
    'search-backend',
    'installer-version',
    'region',
    'target-instance',
    'account-setting',
    'platform-budget',
    'permissions-boundary',
    'image-cache',
    'account-read',
    'region-set-up',
    ...ONBOARDING_STEPS,
];
/**
 * The checks computed from the request alone — the run's own document and the
 * records this control plane keeps — which the console also computes itself,
 * as the document is typed, with the same functions.
 *
 * `installer-version` is the one with an account half: it compares the typed
 * chart version with the region's installer image (IM-196). The form holds that
 * image from the same onboarding read and compares on the keystroke, which is
 * the point — the preflight is asked again only when the region or the
 * instance changes, so its copy would go stale the moment the version is
 * edited.
 *
 * Named so the console can render the preflight's *other* answers beside its
 * own without saying everything twice, and so it waits on no request for a
 * refusal it could have given on the keystroke.
 */
export const DOCUMENT_CHECK_IDS = new Set([
    'refused-variables',
    'required-variables',
    'deployment-id',
    'search-backend',
    'installer-version',
    'region',
    'target-instance',
]);
/**
 * The run's own document, and what this control plane knows about its target.
 *
 * The messages are the validators' own, so the preflight, the form and the
 * start path's 400 say the same sentence.
 */
const runChecks = (input, registryKeyFromAccount) => {
    const checks = [];
    const run = (id, title, problem, passed, fix) => problem === undefined
        ? { id, title, basis: 'run', severity: 'blocking', status: 'pass', detail: passed }
        : { id, title, basis: 'run', severity: 'blocking', status: 'fail', detail: problem, fix };
    checks.push(run('refused-variables', 'Variables the product refuses', refusedVariableProblem(input.variables), 'None set', 'Remove that variable from the run. There is no setting that permits it: the installer acts as the role it assumes inside the container, and nothing else.'));
    /*
     * Whether the registry key is needed turns on the region's dispatcher
     * (IM-118), so it is the one document check with an account half. When that
     * half is unknown the key's absence is reported unknown rather than guessed
     * either way: blocking would refuse a run the account can supply, and
     * passing would let one die at image pull.
     */
    const missing = missingRequiredVariables(input.variables, {
        registryKeyFromAccount: registryKeyFromAccount ?? true,
    });
    const needsKeyUnknown = registryKeyFromAccount === undefined &&
        missingRequiredVariables(input.variables).includes('registry_key');
    if (missing.length > 0) {
        checks.push({
            id: 'required-variables',
            title: 'Required variables',
            basis: 'run',
            severity: 'blocking',
            status: 'fail',
            detail: `Missing ${missing.join(', ')}`,
            fix: `Fill in ${missing
                .map((name) => name === 'customer_info'
                ? 'customer_info (first name, last name, email and company)'
                : name)
                .join(' and ')}. The installer refuses to start without ${missing.length === 1 ? 'it' : 'them'}.`,
        });
    }
    else if (needsKeyUnknown) {
        checks.push({
            id: 'required-variables',
            title: 'Required variables',
            basis: 'run',
            severity: 'advisory',
            status: 'unknown',
            detail: `No registry_key, and whether ${input.region}'s dispatcher supplies one could not be read`,
            fix: `Type the registry key. If ${input.region}'s dispatcher stack in ${input.accountName} holds registry credentials it is not needed, but that could not be confirmed.`,
        });
    }
    else {
        checks.push({
            id: 'required-variables',
            title: 'Required variables',
            basis: 'run',
            severity: 'blocking',
            status: 'pass',
            detail: 'All present',
        });
    }
    checks.push(run('deployment-id', 'Deployment id', deploymentIdProblem(input.variables['deployment_id']) ??
        // A new instance is held to MP-3693's narrower grammar (IM-192); one
        // that exists keeps the id it has.
        (input.instance.exists ? undefined : newDeploymentIdProblem(input.instance.named)), input.instance.named === undefined ? 'None named; one is generated' : input.instance.named, 'Use 1 to 16 lowercase letters and digits for a new instance (one that exists keeps up to 18), or leave it blank and one is generated.'));
    checks.push(run('search-backend', 'Search backend', searchBackendProblem(input.variables), input.variables['opensearch_config'] !== undefined
        ? 'AWS OpenSearch'
        : input.variables['es_config'] !== undefined
            ? 'Elasticsearch in the cluster'
            : 'None chosen', 'Send exactly one of es_config or opensearch_config, with every field it names.'));
    const requested = input.requestedRegion;
    const shape = requested === undefined ? undefined : regionProblem(requested);
    const conflict = requested === undefined || shape !== undefined
        ? undefined
        : regionConflict(input.existingRegion, requested);
    checks.push(run('region', 'Region', shape ?? conflict, `Runs in ${input.region}`, conflict !== undefined && input.existingRegion !== undefined
        ? `Run it in ${input.existingRegion}, where its state is, or use a new deployment id for a separate instance.`
        : `Choose a region ${input.accountName} is set up in, such as us-east-1.`));
    checks.push(run('target-instance', 'Instance', targetInstanceProblem(input.command, input.instance.named, input.instance.exists), input.instance.named === undefined
        ? 'A new instance'
        : input.instance.exists
            ? `${input.instance.named}, which exists`
            : `${input.instance.named}, new`, input.instance.named === undefined
        ? `Start the ${input.command} from the instance's own screen, which fixes the id.`
        : `Check the id against ${input.accountName}'s instances. A ${input.command} of an id with no state does nothing and reports success.`));
    return checks;
};
/**
 * Whether a run's command can act on the instance it names.
 *
 * A destroy or an output must name an instance that exists: minting an id for
 * one produced a key no state file had ever been written under, so a destroy
 * found nothing, destroyed nothing and **succeeded** while the real instance
 * kept running (`isno0toh`). The start path refuses on this function, and so
 * does the preflight, with one wording.
 */
export const targetInstanceProblem = (command, named, exists) => {
    if (mayCreateInstance(command))
        return undefined;
    if (named === undefined)
        return `A ${command} must name the deployment it is for.`;
    if (!exists)
        return `No deployment ${named} in this account.`;
    return undefined;
};
const POLICY_TITLE = {
    'account-setting': 'Account takes new instances',
    'platform-budget': 'Platform budget',
};
const policyCheck = (outcome, accountName) => {
    const title = POLICY_TITLE[outcome.id];
    if (outcome.refusal === undefined) {
        return {
            id: outcome.id,
            title,
            basis: 'run',
            severity: 'blocking',
            status: 'pass',
            detail: outcome.id === 'account-setting' ? 'Not blocked' : 'Not over the budget',
        };
    }
    switch (outcome.id) {
        case 'account-setting':
            return {
                id: outcome.id,
                title,
                basis: 'run',
                severity: 'blocking',
                status: 'fail',
                detail: outcome.refusal,
                fix: `Ask ${outcome.setBy ?? 'whoever set it'} to let ${accountName} take new instances again, on its onboarding screen — or apply to an instance that already exists.`,
            };
        case 'platform-budget':
            return {
                id: outcome.id,
                title,
                basis: 'run',
                severity: 'blocking',
                status: 'fail',
                detail: outcome.refusal,
                fix: 'Destroy an instance nobody needs, or have a platform admin raise the budget or stop it blocking, on the budget screen.',
            };
        default:
            return assertNever(outcome.id, 'policy check');
    }
};
/**
 * The steps whose failure clears `canRun`. Only these can be blocking, and a
 * pass on one of them carries that class; the rest are advisory by nature.
 */
const GATING_STEPS = new Set([
    'access-template',
    'registry-credentials',
    'dispatcher',
    'grant',
]);
/**
 * An onboarding step's next action, re-addressed for a screen that is not the
 * onboarding screen.
 *
 * The steps say "from this screen" because that is where they are read. Here
 * they are read on the run form, where no template is offered, so the fix
 * names the screen that has it. Every fix is already prefixed with the account
 * and region it is about, so this does not name the account a second time. A
 * test pins that no fix says "this screen".
 */
const addressed = (action) => action.replaceAll('from this screen', "from the account's onboarding screen");
const stepCheck = (step, blockedBy, where) => {
    const base = { id: step.id, title: step.title, basis: 'account' };
    const detail = step.detail === '' ? `${step.title}: nothing could be read` : step.detail;
    switch (step.state) {
        case 'done':
            return {
                ...base,
                severity: GATING_STEPS.has(step.id) ? 'blocking' : 'advisory',
                status: 'pass',
                detail,
            };
        case 'unknown':
            return {
                ...base,
                severity: 'advisory',
                status: 'unknown',
                detail,
                fix: `${where}: ${step.nextAction === undefined
                    ? "open the account's onboarding screen to see why this could not be read. The run may still be refused by the account."
                    : addressed(step.nextAction)}`,
            };
        case 'attention':
            return {
                ...base,
                severity: blockedBy.includes(step.id) ? 'blocking' : 'advisory',
                status: 'fail',
                detail,
                fix: `${where}: ${addressed(step.nextAction ?? "see the account's onboarding screen.")}`,
            };
        default:
            return assertNever(step.state, 'step state');
    }
};
const accountChecks = (input, account) => {
    const { assessment } = account;
    const checks = assessment.accountSteps.map((s) => stepCheck(s, assessment.accountBlockedBy, input.accountName));
    const region = assessment.regions.find((r) => r.region === input.region);
    const where = `${input.accountName}, ${input.region}`;
    if (region === undefined) {
        /*
         * A region is discovered by its state bucket, so one that is not listed has
         * nowhere to keep this run's state — the dispatcher's task definition names
         * that bucket, and there is no dispatcher either. Certain to fail, so
         * blocking; unless the list itself could not be read, which is unknown.
         */
        checks.push(account.regionsListed
            ? {
                id: 'region-set-up',
                title: 'Region set up',
                basis: 'account',
                severity: 'blocking',
                status: 'fail',
                detail: `${input.region} has no state bucket, so it is not set up in this account`,
                fix: `Deploy the region template and then the dispatcher template in ${input.region}, from ${input.accountName}'s onboarding screen — in that order, because the dispatcher names the region stack's state bucket.`,
            }
            : {
                id: 'region-set-up',
                title: 'Region set up',
                basis: 'account',
                severity: 'advisory',
                status: 'unknown',
                detail: `Could not list the regions ${input.accountName} is set up in`,
                fix: `${where}: open the account's onboarding screen to see why its regions could not be read. The run may still be refused if ${input.region} has no region stack.`,
            });
        return checks;
    }
    checks.push({
        id: 'region-set-up',
        title: 'Region set up',
        basis: 'account',
        severity: 'blocking',
        status: 'pass',
        detail: `${input.region} has a state bucket`,
    });
    for (const step of region.steps) {
        checks.push(stepCheck(step, region.blockedBy, STEP_SCOPE[step.id] === 'account' ? input.accountName : where));
    }
    return checks;
};
/**
 * Whether the chart version asked for goes with the region's installer
 * (IM-196). Never blocking: a hotfix may ship for one side only.
 *
 * Absent when the run names no chart version, or the region is not one the
 * account is set up in — `region-set-up` already says that, and there is no
 * installer to compare with.
 */
const installerVersionCheck = (input, account) => {
    const chart = input.variables['qrvey_chart_version'];
    if (typeof chart !== 'string' || chart.trim() === '')
        return undefined;
    const region = account.assessment.regions.find((r) => r.region === input.region);
    if (region === undefined)
        return undefined;
    const match = chartInstallerMatch(chart, region.runnerImage);
    const message = installerMatchMessage(chart, match, input.region);
    const base = {
        id: 'installer-version',
        title: 'Chart and installer versions',
        basis: 'run',
        severity: 'advisory',
        detail: message.text,
    };
    switch (message.level) {
        case 'none':
            return { ...base, status: 'pass' };
        case 'note':
        case 'warning':
            return {
                ...base,
                status: 'fail',
                fix: match.kind === 'same-line' || match.kind === 'different-line'
                    ? `Unless this is a deliberate hotfix, set qrvey_chart_version to ${match.tag}, the version of the installer in ${input.region}.`
                    : 'Use the same version for the chart and the installer.',
            };
        case 'unknown':
            return {
                ...base,
                status: 'unknown',
                fix: `See ${input.region}'s dispatcher on ${input.accountName}'s onboarding screen for the installer it runs. The run can still be started.`,
            };
        default:
            return assertNever(message.level, 'installer match level');
    }
};
/** The preflight for one proposed run. Pure: every read happens before it. */
export const preflight = (input) => {
    const readiness = input.account === 'unreadable' ? undefined : input.account;
    const registryKeyFromAccount = readiness === undefined
        ? undefined
        : readiness.assessment.regions.find((r) => r.region === input.region)?.suppliesRegistryKey;
    const checks = runChecks(input, registryKeyFromAccount);
    for (const outcome of input.policies ?? [])
        checks.push(policyCheck(outcome, input.accountName));
    /*
     * A boundary, when the account requires one (IM-194). Only for an apply,
     * which is all the rule holds; and only reported where it decides
     * something, so an account that does not require boundaries shows nothing.
     */
    if (input.boundary !== undefined &&
        input.boundary.mode === 'required' &&
        input.command === 'apply') {
        const problem = requiredBoundaryProblem(input.command, input.boundary.mode, input.boundary.recorded);
        checks.push({
            id: 'permissions-boundary',
            title: 'Permissions boundary',
            basis: 'run',
            severity: 'blocking',
            ...(problem === undefined
                ? { status: 'pass', detail: 'Recorded, and every run carries it' }
                : {
                    status: 'fail',
                    detail: problem,
                    fix: 'Record the instance’s boundary, then ask again.',
                }),
        });
    }
    /*
     * The ECR image cache (MP-3601). Never blocking: a run that cannot keep the
     * cache is sent Qrvey's registry and goes ahead, so this only says which.
     */
    if (input.imagePull !== undefined) {
        const { override } = input.imagePull;
        checks.push({
            id: 'image-cache',
            title: 'ECR image cache',
            basis: 'run',
            severity: 'advisory',
            ...(override === undefined
                ? { status: 'pass', detail: 'The run keeps job images in this account’s ECR' }
                : {
                    status: 'fail',
                    detail: override.why,
                    fix: `Update ${input.accountName}'s access stack to the current template to use the cache. The run can be started as it is, without it.`,
                }),
        });
    }
    if (input.account === 'unreadable') {
        checks.push({
            id: 'account-read',
            title: 'Account readable',
            basis: 'account',
            severity: 'advisory',
            status: 'unknown',
            detail: `Could not read ${input.accountName}: nothing is known about its grant, dispatcher or regions`,
            fix: `Open ${input.accountName}'s onboarding screen to see why. The diagnostics role may be missing, or its external ID may not match. The run may still be refused by the account.`,
        });
    }
    else if (readiness !== undefined) {
        const installer = installerVersionCheck(input, readiness);
        if (installer !== undefined)
            checks.push(installer);
        checks.push(...accountChecks(input, readiness));
    }
    return {
        command: input.command,
        region: input.region,
        checks,
        blocking: checks.some((c) => c.severity === 'blocking' && c.status === 'fail'),
    };
};
//# sourceMappingURL=preflight.js.map